import { createContext, useContext, useReducer } from 'react';
import { DEFAULT_DRAW_COLOUR } from '@utils/colours';

const AppContext = createContext(null);

const initialState = {
  user: { id: null, nickname: '', colour: '', isHost: false },
  room: { code: null, name: '', locked: false, members: {}, hostId: null },
  canvas: {
    tool: 'pencil',
    colour: DEFAULT_DRAW_COLOUR,
    size: 6,
    opacity: 1,
    background: '#FAF8F5',
    allStrokes: [],
    localStrokeIds: [],
    activeStroke: null,
    soloMode: false,
    soloStrokes: [],
    soloStrokeIds: [],
    activeLayer: 'foreground', // 'foreground' | 'background'
    activeStamp: { id: 'star', emoji: '⭐' },
    secondaryColour: '#FAF8F5',
    selectedStrokeIds: [],
    clipMaskId: null,
    clipboard: [],
    redoStack: [],
    savedSharedStrokes: null,
    activeStyle: null, // Art style preset: { id, name, emoji, colour, palette, toolPreset }
  },
  game: {
    mode: null,           // 'challenge' | 'relay' | null
    phase: 'off',         // varies by mode
    round: 1,
    timerEndsAt: null,
    timerDuration: null,
    hostId: null,
    startedAt: null,
    // Challenge-specific
    challenge: null,
    submissions: {},
    votes: {},
    scores: {},
    drawoffEligible: [],
    winner: null,
    // Relay-specific
    turnOrder: [],
    currentDrawerId: null,
    totalRounds: 1,
    theme: null,
    blindRelay: false,
    turnHistory: [],
    reactions: [],
  },
  cursors: {},
  ui: {
    screen: 'landing',
    membersOpen: false,
    hostPanelOpen: false,
    focusMode: false,
    toasts: [],
  },
};

let toastId = 0;

function reducer(state, action) {
  switch (action.type) {
    case 'SET_USER':
      return { ...state, user: { ...state.user, ...action.payload } };

    case 'SET_ROOM_META': {
      const newState = {
        ...state,
        room: {
          ...state.room,
          name: action.payload.roomName || state.room.name,
          locked: action.payload.locked || false,
          hostId: action.payload.hostId || state.room.hostId,
        },
        user: {
          ...state.user,
          isHost: action.payload.hostId === state.user.id,
        },
      };
      // Sync background colour from room meta if present
      if (action.payload.background) {
        newState.canvas = { ...state.canvas, background: action.payload.background };
      }
      return newState;
    }

    case 'SET_MEMBERS':
      return { ...state, room: { ...state.room, members: action.payload } };

    case 'SET_CURSORS':
      return { ...state, cursors: action.payload };

    case 'SET_SCREEN':
      return { ...state, ui: { ...state.ui, screen: action.payload } };

    case 'SET_ROOM_CODE':
      return { ...state, room: { ...state.room, code: action.payload } };

    case 'SET_TOOL':
      return { ...state, canvas: { ...state.canvas, tool: action.payload } };

    case 'SET_COLOUR':
      return { ...state, canvas: { ...state.canvas, colour: action.payload } };

    case 'SET_SIZE':
      return { ...state, canvas: { ...state.canvas, size: action.payload } };

    case 'SET_OPACITY':
      return { ...state, canvas: { ...state.canvas, opacity: action.payload } };

    case 'SET_BACKGROUND':
      return { ...state, canvas: { ...state.canvas, background: action.payload } };

    case 'TOGGLE_SOLO_MODE':
      return { ...state, canvas: { ...state.canvas, soloMode: !state.canvas.soloMode } };

    case 'ADD_SOLO_STROKE':
      return {
        ...state,
        canvas: {
          ...state.canvas,
          soloStrokes: [...state.canvas.soloStrokes, action.payload],
          soloStrokeIds: [...state.canvas.soloStrokeIds, action.payload.id],
          activeStroke: null,
        },
      };

    case 'REMOVE_SOLO_STROKE':
      return {
        ...state,
        canvas: {
          ...state.canvas,
          soloStrokes: state.canvas.soloStrokes.filter(s => s.id !== action.payload),
          soloStrokeIds: state.canvas.soloStrokeIds.filter(id => id !== action.payload),
        },
      };

    case 'CLEAR_SOLO_STROKES':
      return {
        ...state,
        canvas: { ...state.canvas, soloStrokes: [], soloStrokeIds: [] },
      };

    case 'SHARE_SOLO_STROKES':
      return {
        ...state,
        canvas: {
          ...state.canvas,
          allStrokes: [...state.canvas.allStrokes, ...state.canvas.soloStrokes],
          localStrokeIds: [...state.canvas.localStrokeIds, ...state.canvas.soloStrokeIds],
          soloStrokes: [],
          soloStrokeIds: [],
          soloMode: false,
        },
      };

    case 'SET_ACTIVE_LAYER':
      return { ...state, canvas: { ...state.canvas, activeLayer: action.payload } };

    case 'SET_STAMP':
      return { ...state, canvas: { ...state.canvas, activeStamp: action.payload } };

    case 'SET_SECONDARY_COLOUR':
      return { ...state, canvas: { ...state.canvas, secondaryColour: action.payload } };

    case 'SET_SELECTED_STROKES':
      return { ...state, canvas: { ...state.canvas, selectedStrokeIds: action.payload } };

    case 'CLEAR_SELECTION':
      return { ...state, canvas: { ...state.canvas, selectedStrokeIds: [] } };

    case 'SET_CLIP_MASK':
      return { ...state, canvas: { ...state.canvas, clipMaskId: action.payload } };

    case 'UPDATE_STROKE': {
      const { id, updates } = action.payload;
      return {
        ...state,
        canvas: {
          ...state.canvas,
          allStrokes: state.canvas.allStrokes.map(s =>
            s.id === id ? { ...s, ...updates } : s
          ),
        },
      };
    }

    case 'UPDATE_STROKES_BATCH': {
      const updateMap = new Map(action.payload.map(u => [u.id, u.updates]));
      return {
        ...state,
        canvas: {
          ...state.canvas,
          allStrokes: state.canvas.allStrokes.map(s =>
            updateMap.has(s.id) ? { ...s, ...updateMap.get(s.id) } : s
          ),
        },
      };
    }

    case 'SET_REPLAY_STROKES':
      return { ...state, canvas: { ...state.canvas, allStrokes: action.payload } };

    case 'RESTORE_STROKES':
      return { ...state, canvas: { ...state.canvas, allStrokes: action.payload } };

    case 'SET_ACTIVE_STROKE':
      return { ...state, canvas: { ...state.canvas, activeStroke: action.payload } };

    case 'ADD_STROKE': {
      // Deduplicate — skip if we already have this stroke
      const exists = state.canvas.allStrokes.some(s => s.id === action.payload.id);
      if (exists) return state;
      return {
        ...state,
        canvas: {
          ...state.canvas,
          allStrokes: [...state.canvas.allStrokes, action.payload],
        },
      };
    }

    case 'COMMIT_STROKE':
      return {
        ...state,
        canvas: {
          ...state.canvas,
          allStrokes: [...state.canvas.allStrokes, action.payload],
          localStrokeIds: [...state.canvas.localStrokeIds, action.payload.id],
          activeStroke: null,
          redoStack: [],
        },
      };

    case 'REMOVE_STROKE': {
      const removedStroke = state.canvas.allStrokes.find(s => s.id === action.payload);
      return {
        ...state,
        canvas: {
          ...state.canvas,
          allStrokes: state.canvas.allStrokes.filter(s => s.id !== action.payload),
          localStrokeIds: state.canvas.localStrokeIds.filter(id => id !== action.payload),
          redoStack: removedStroke ? [...state.canvas.redoStack, removedStroke] : state.canvas.redoStack,
        },
      };
    }

    case 'REDO_STROKE':
      return {
        ...state,
        canvas: {
          ...state.canvas,
          allStrokes: [...state.canvas.allStrokes, action.payload],
          localStrokeIds: [...state.canvas.localStrokeIds, action.payload.id],
          redoStack: state.canvas.redoStack.slice(0, -1),
        },
      };

    case 'COPY_SELECTED': {
      const copied = state.canvas.allStrokes.filter(s =>
        state.canvas.selectedStrokeIds.includes(s.id)
      );
      return { ...state, canvas: { ...state.canvas, clipboard: copied } };
    }

    case 'CLEAR_CLIPBOARD':
      return { ...state, canvas: { ...state.canvas, clipboard: [] } };

    case 'PASTE_CLIPBOARD':
      return {
        ...state,
        canvas: {
          ...state.canvas,
          allStrokes: [...state.canvas.allStrokes, ...action.payload],
          localStrokeIds: [...state.canvas.localStrokeIds, ...action.payload.map(s => s.id)],
          selectedStrokeIds: action.payload.map(s => s.id),
        },
      };

    case 'CLEAR_MY_STROKES':
      return {
        ...state,
        canvas: {
          ...state.canvas,
          allStrokes: state.canvas.allStrokes.filter(s => s.userId !== action.payload),
          localStrokeIds: [],
        },
      };

    case 'CLEAR_ALL_STROKES':
      return {
        ...state,
        canvas: {
          ...state.canvas,
          allStrokes: [],
          localStrokeIds: [],
          activeStroke: null,
        },
      };

    case 'TOGGLE_MEMBERS':
      return { ...state, ui: { ...state.ui, membersOpen: !state.ui.membersOpen } };

    case 'TOGGLE_HOST_PANEL':
      return { ...state, ui: { ...state.ui, hostPanelOpen: !state.ui.hostPanelOpen } };

    case 'TOGGLE_FOCUS_MODE':
      return { ...state, ui: { ...state.ui, focusMode: !state.ui.focusMode } };

    case 'SET_FOCUS_MODE':
      return { ...state, ui: { ...state.ui, focusMode: !!action.payload } };

    case 'ADD_TOAST': {
      const toast = { id: ++toastId, ...action.payload };
      return {
        ...state,
        ui: { ...state.ui, toasts: [...state.ui.toasts.slice(-2), toast] },
      };
    }

    case 'REMOVE_TOAST':
      return {
        ...state,
        ui: { ...state.ui, toasts: state.ui.toasts.filter(t => t.id !== action.payload) },
      };

    // --- Art style preset ---
    case 'SET_ACTIVE_STYLE':
      return { ...state, canvas: { ...state.canvas, activeStyle: action.payload } };

    case 'CLEAR_ACTIVE_STYLE':
      return { ...state, canvas: { ...state.canvas, activeStyle: null } };

    // --- Game state ---
    case 'SET_GAME_STATE':
      return { ...state, game: { ...state.game, ...action.payload } };

    case 'CLEAR_GAME':
      return {
        ...state,
        game: { ...initialState.game },
        // Ensure soloMode is off — Challenge/Telephone toggle it on during their
        // drawing phases, and without this reset it leaks into subsequent game modes.
        canvas: { ...state.canvas, soloMode: false },
      };

    case 'SAVE_SHARED_STROKES':
      return {
        ...state,
        canvas: { ...state.canvas, savedSharedStrokes: [...state.canvas.allStrokes] },
      };

    case 'RESTORE_SHARED_STROKES':
      return {
        ...state,
        canvas: {
          ...state.canvas,
          allStrokes: state.canvas.savedSharedStrokes || [],
          savedSharedStrokes: null,
          soloMode: false,
          soloStrokes: [],
          soloStrokeIds: [],
        },
      };

    case 'RESET':
      return { ...initialState, user: { ...initialState.user, id: state.user.id, nickname: state.user.nickname } };

    default:
      return state;
  }
}

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within AppProvider');
  return context;
}
